const RETURN_PARAM = 'returnTo';
const OAUTH_RETURN_KEY = 'mora:settings-return';
const OAUTH_RETURN_TTL_MS = 15 * 60 * 1000;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;
const NEVER_RETURN_TO = ['/settings', '/auth', '/login', '/signup', '/oauth'];

/** An address inside the app that a Settings exit may come back to; never Settings, sign-in, or another origin. */
export function safeReturnTo(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  if (value.includes(':') || value.includes('\\') || CONTROL_CHARACTER.test(value)) return null;
  const path = value.split('?')[0].toLowerCase();
  if (NEVER_RETURN_TO.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return null;
  }
  return value;
}

/** The address of a Settings section that returns to `returnTo` when it is closed. */
export function settingsHref(section: string, returnTo?: string | null): string {
  const target = safeReturnTo(returnTo);
  return target
    ? `/settings/${section}?${RETURN_PARAM}=${encodeURIComponent(target)}`
    : `/settings/${section}`;
}

export function readReturnTo(search: string): string | null {
  return safeReturnTo(new URLSearchParams(search).get(RETURN_PARAM));
}

/**
 * A connection that leaves the app and comes back (Google's consent screen) lands on `/settings/*` with
 * no memory of the panel it started from. The address is kept for fifteen minutes in this tab.
 */
export function rememberSettingsReturn(address: string | null, nowMs: number = Date.now()): void {
  try {
    if (address && safeReturnTo(address)) {
      window.sessionStorage.setItem(OAUTH_RETURN_KEY, JSON.stringify({ address, at: nowMs }));
    } else {
      window.sessionStorage.removeItem(OAUTH_RETURN_KEY);
    }
  } catch {
    return;
  }
}

export function takeSettingsReturn(nowMs: number = Date.now()): string | null {
  try {
    const stored = window.sessionStorage.getItem(OAUTH_RETURN_KEY);
    window.sessionStorage.removeItem(OAUTH_RETURN_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { address?: unknown; at?: unknown };
    if (typeof parsed.address !== 'string' || typeof parsed.at !== 'number') return null;
    if (nowMs - parsed.at > OAUTH_RETURN_TTL_MS) return null;
    return safeReturnTo(parsed.address);
  } catch {
    return null;
  }
}
